import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from './http.service';
import { ApiResponse } from '../interfaces/api-response';
import {
  TwoFactorEnableData,
  TwoFactorMethodsData,
  TwoFactorRecoveryCodesData,
  TwoFactorRemoveMethodData,
  TwoFactorSetupData,
  TwoFactorStatusData,
} from '../interfaces/two-factor';

/**
 * Self-service 2FA calls for the profile page (HD-2013).
 *
 * Errors are NOT caught here. The error interceptor already reshapes them
 * into { status, message, errors } and callers map that with
 * mapTwoFactorError(), so the status and the backend message stay intact.
 */
@Injectable({
  providedIn: 'root',
})
export class TwoFactorService {
  private readonly BASE_PATH = 'v2/extranet/2fa';

  constructor(private http: HttpService) {}

  getStatus(): Observable<ApiResponse<TwoFactorStatusData>> {
    return this.http.get<ApiResponse<TwoFactorStatusData>>(`${this.BASE_PATH}/status`);
  }

  /** Confirmed authenticators only. Allowed while impersonating. */
  getMethods(): Observable<ApiResponse<TwoFactorMethodsData>> {
    return this.http.get<ApiResponse<TwoFactorMethodsData>>(`${this.BASE_PATH}/methods`);
  }

  /**
   * Step 1 of adding an authenticator: creates the pending method and returns
   * its secret and QR code. The password is always required.
   */
  generate(label: string, password: string): Observable<ApiResponse<TwoFactorSetupData>> {
    return this.http.post<ApiResponse<TwoFactorSetupData>>(`${this.BASE_PATH}/generate`, { label, password });
  }

  /**
   * Step 2: confirms the pending method with a code from the new phone.
   * methodId is the method_id that generate returned, so several people on
   * one shared login can each confirm their own setup at the same time.
   * The response carries recovery codes only for the first authenticator.
   */
  enable(methodId: number, code: string): Observable<ApiResponse<TwoFactorEnableData>> {
    return this.http.post<ApiResponse<TwoFactorEnableData>>(`${this.BASE_PATH}/enable`, { method_id: methodId, code });
  }

  /** Removes one authenticator. enabled=false in the response means 2FA is now off. */
  removeMethod(methodId: number, password: string): Observable<ApiResponse<TwoFactorRemoveMethodData>> {
    return this.http.delete<ApiResponse<TwoFactorRemoveMethodData>>(`${this.BASE_PATH}/methods/${methodId}`, {
      body: { password },
    });
  }

  /** Removes every authenticator, the recovery codes and all trusted devices. */
  disable(password: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.BASE_PATH}/disable`, { password });
  }

  getRecoveryCodes(password: string): Observable<ApiResponse<TwoFactorRecoveryCodesData>> {
    return this.http.post<ApiResponse<TwoFactorRecoveryCodesData>>(`${this.BASE_PATH}/recovery-codes`, { password });
  }

  /** Previous recovery codes stop working once new ones are generated. */
  regenerateRecoveryCodes(password: string): Observable<ApiResponse<TwoFactorRecoveryCodesData>> {
    return this.http.post<ApiResponse<TwoFactorRecoveryCodesData>>(
      `${this.BASE_PATH}/recovery-codes/regenerate`,
      { password }
    );
  }
}
